import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  addCampaign,
  addCampaignTemplate,
  deleteCampaign,
  deleteCampaignTemplate,
  fetchAllCampaigns,
  fetchAllCampaignTemplates,
  setSelectedDate,
  setTemplate,
  setShowTemplatePreview,
  updateCampaign,
  updateCampaignTemplate,
  setTemplateError,
  bulkDeleteCampaignRecipient,
  bulkAddCampaignRecipient,
  bulkAddKeywords,
  bulkUpdateKeywords,
  bulkAddTriggerMessages,
  bulkDeleteTriggerMessages,
  bulkDeleteKeywords,
  bulkUpdateTriggerMessages,
  updateCampaignMessage,
  sendCampaignSms,
  scheduleSendSms,
  sendCampaignEmail,
  scheduleSendEmail,
  resendBouncedSms,
  optOutCampaignRecipient,
} from '@store/actions'
import {
  CampaignMessageStatus,
  CampaignTemplateType,
  ICampaignMessage,
  ICampaignRecipient,
  ICampaignRequest,
  ICampaignResponse,
  ICampaignTemplate,
  ICampaignTriggerKeyword,
  ICampaignTriggerMessage,
  marketingOverviewFilter,
} from '@dataTypes/marketing.type'
import { StatusTypes } from '@dataTypes/claimFeature.types'

interface CampaignState {
  campaigns: ICampaignResponse[]
  campaignTemplates: ICampaignTemplate[]
  selectedDate: string
  showTemplatePreview: boolean
  /** Templates list SMS / Email toggle — survives leaving the template editor */
  templatesListChannel: 'SMS' | 'Email'
  /** Incremented when user clicks navbar Preview; editor sets non-editing preview. */
  templatePreviewPulse: number
  /** Incremented when user clicks navbar Edit while in read-only preview. */
  templateEditorResumePulse: number
  /** True when template canvas shows HTML preview (not RTE). Navbar uses for button label. */
  overviewDateFilter: marketingOverviewFilter
  templateEditorCanvasPreview: boolean
  selectedTemplate: ICampaignTemplate | null
  currentTemplate: ICampaignTemplate | null
  loading: {
    fetch: boolean
    add: boolean
    update: boolean
    delete: boolean
  }
  error: {
    fetch: string | null
    add: string | null
    update: string | null
    delete: string | null
    template: string | null
    hyperlink: string | null
  }
}

const initialState: CampaignState = {
  campaigns: [],
  campaignTemplates: [],
  selectedDate: new Date().toISOString(),
  showTemplatePreview: false,
  templatesListChannel: 'SMS',
  templatePreviewPulse: 0,
  templateEditorResumePulse: 0,
  templateEditorCanvasPreview: false,
  overviewDateFilter: marketingOverviewFilter['1D'],
  selectedTemplate: null,
  currentTemplate: null,
  loading: {
    fetch: false,
    add: false,
    update: false,
    delete: false,
  },
  error: {
    fetch: null,
    add: null,
    update: null,
    delete: null,
    template: null,
    hyperlink: null,
  },
}

const appendMessagesWithContact = (
  campaigns: ICampaignResponse[],
  campaignId: string | undefined,
  newMessages: ICampaignMessage[],
): ICampaignResponse[] =>
  campaigns.map((c) => {
    if (c.campaign_id !== campaignId) return c
    const contactById = new Map(
      c.campaign_recipient.map((r) => [r.contact_id, r.contact]),
    )
    const enriched = newMessages.map((m) => ({
      ...m,
      contact: m.contact ?? contactById.get(m.contact_id),
    }))
    return { ...c, campaign_message: [...c.campaign_message, ...enriched] }
  })

const marketingSlice = createSlice({
  name: 'marketing',
  initialState,
  reducers: {
    /** SMS vs Email tab on Templates list — restored after closing template editor (e.g. Back to templates). */
    setTemplatesListChannel(state, action: PayloadAction<'SMS' | 'Email'>) {
      state.templatesListChannel = action.payload
    },
    /** Bumps so Template editor listens and switches to HTML preview (non-editing) mode. */
    pulseTemplateEditorPreview(state) {
      state.templatePreviewPulse += 1
    },
    /** Bumps so Template editor returns to rich-text editing mode (navbar “Edit”). */
    pulseTemplateEditorResumeEdit(state) {
      state.templateEditorResumePulse += 1
    },
    /** Mirrors TemplatePreview `isEditing` for Marketing navbar (Preview vs Edit label). */
    setTemplateEditorCanvasPreview(state, action: PayloadAction<boolean>) {
      state.templateEditorCanvasPreview = action.payload
    },
    setOverviewDateFilter(
      state,
      action: PayloadAction<marketingOverviewFilter>,
    ) {
      state.overviewDateFilter = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllCampaigns.pending, (state) => {
        state.loading.fetch = true
        state.error.fetch = null
      })
      .addCase(
        fetchAllCampaigns.fulfilled,
        (state, action: PayloadAction<ICampaignResponse[]>) => {
          state.campaigns = action.payload
          state.loading.fetch = false
        },
      )
      .addCase(fetchAllCampaigns.rejected, (state, action) => {
        state.error.fetch = action.error.message || 'Failed to fetch campaigns'
        state.loading.fetch = false
      })

    builder
      .addCase(fetchAllCampaignTemplates.pending, (state) => {
        state.loading.fetch = true
        state.error.fetch = null
      })
      .addCase(
        fetchAllCampaignTemplates.fulfilled,
        (state, action: PayloadAction<ICampaignTemplate[]>) => {
          state.campaignTemplates = action.payload
          state.loading.fetch = false
        },
      )
      .addCase(fetchAllCampaignTemplates.rejected, (state, action) => {
        state.error.fetch =
          action.error.message || 'Failed to fetch campaign templates'
        state.loading.fetch = false
      })

    builder
      .addCase(addCampaign.pending, (state) => {
        state.loading.add = true
        state.error.add = null
      })
      .addCase(
        addCampaign.fulfilled,
        (state, action: PayloadAction<ICampaignRequest>) => {
          state.campaigns.push({
            ...action.payload,
            platform: action.payload.channel,
            sent: 0,
            open: 0,
            clicks: 0,
            uploadTime: new Date().toISOString(),
            uploadDate: new Date().toISOString(),
            statusType: StatusTypes.Pending,
          })
          state.loading.add = false
        },
      )
      .addCase(addCampaign.rejected, (state, action) => {
        state.loading.add = false
        state.error.add = action.payload as string
      })

    builder
      .addCase(addCampaignTemplate.pending, (state) => {
        state.loading.add = true
        state.error.add = null
      })
      .addCase(addCampaignTemplate.fulfilled, (state) => {
        state.loading.add = false
      })
      .addCase(addCampaignTemplate.rejected, (state, action) => {
        state.loading.add = false
        state.error.add = action.payload as string
      })

    builder
      .addCase(bulkAddCampaignRecipient.pending, (state) => {
        state.loading.add = true
        state.error.add = null
      })
      .addCase(
        bulkAddCampaignRecipient.fulfilled,
        (state, action: PayloadAction<ICampaignRecipient[]>) => {
          state.loading.add = false
          const cmp = state.campaigns.find(
            (a) => a.campaign_id === action.payload[0].campaign_id,
          )
          if (cmp) {
            state.campaigns = state.campaigns.map((c) => ({
              ...c,
              campaign_recipient:
                c.campaign_id == cmp.campaign_id
                  ? [...c.campaign_recipient, ...action.payload]
                  : c.campaign_recipient,
            }))
          }
        },
      )
      .addCase(bulkAddCampaignRecipient.rejected, (state, action) => {
        state.loading.add = false
        state.error.add = action.payload as string
      })

    builder
      .addCase(updateCampaign.pending, (state) => {
        state.loading.update = true
        state.error.update = null
      })
      .addCase(updateCampaign.fulfilled, (state, action) => {
        const index = state.campaigns.findIndex(
          (a) => a.campaign_id === action.payload.campaign_id,
        )
        if (index !== -1) {
          state.campaigns[index] = {
            ...state.campaigns[index],
            ...action.payload,
          }
        }
        state.loading.update = false
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.loading.update = false
        state.error.update = action.payload as string
      })

    builder
      .addCase(updateCampaignTemplate.pending, (state) => {
        state.loading.update = true
        state.error.update = null
      })
      .addCase(updateCampaignTemplate.fulfilled, (state, action) => {
        const index = state.campaignTemplates.findIndex(
          (a) => a.campaign_template_id === action.payload.campaign_template_id,
        )
        if (index !== -1) {
          state.campaignTemplates[index] = {
            ...state.campaignTemplates[index],
            ...action.payload,
          }
        }
        state.loading.update = false
      })
      .addCase(updateCampaignTemplate.rejected, (state, action) => {
        state.loading.update = false
        state.error.update = action.payload as string
      })

    builder
      .addCase(bulkAddKeywords.pending, (state) => {
        state.loading.add = true
        state.error.add = null
      })
      .addCase(
        bulkAddKeywords.fulfilled,
        (state, action: PayloadAction<ICampaignTriggerKeyword[]>) => {
          state.loading.add = false
          const campaignId = action.payload?.[0].campaign_id
          const keywords = action.payload
          state.campaigns = state.campaigns.map((c) => ({
            ...c,
            campaign_trigger_keyword:
              c.campaign_id === campaignId
                ? [...c.campaign_trigger_keyword, ...keywords]
                : c.campaign_trigger_keyword,
          }))
        },
      )
      .addCase(bulkAddKeywords.rejected, (state, action) => {
        state.loading.add = false
        state.error.add = action.payload as string
      })

    builder
      .addCase(bulkUpdateKeywords.pending, (state) => {
        state.loading.update = true
        state.error.update = null
      })
      .addCase(
        bulkUpdateKeywords.fulfilled,
        (state, action: PayloadAction<ICampaignTriggerKeyword[]>) => {
          state.loading.update = false
          const campaignId = action.payload?.[0].campaign_id
          const keywordIds = action.payload.map(
            (k) => k.campaign_trigger_keyword_id,
          )
          const keywords = action.payload
          state.campaigns = state.campaigns.map((c) => ({
            ...c,
            campaign_trigger_keyword:
              c.campaign_id === campaignId
                ? [
                    ...c.campaign_trigger_keyword.filter(
                      (k) =>
                        !keywordIds.includes(k.campaign_trigger_keyword_id),
                    ),
                    ...keywords,
                  ]
                : c.campaign_trigger_keyword,
          }))
        },
      )
      .addCase(bulkUpdateKeywords.rejected, (state, action) => {
        state.loading.update = false
        state.error.update = action.payload as string
      })

    builder
      .addCase(bulkAddTriggerMessages.pending, (state) => {
        state.loading.add = true
        state.error.add = null
      })
      .addCase(
        bulkAddTriggerMessages.fulfilled,
        (state, action: PayloadAction<ICampaignTriggerMessage[]>) => {
          state.loading.add = false
          const campaignId = action.payload?.[0].campaign_id
          const messages = action.payload
          state.campaigns = state.campaigns.map((c) => ({
            ...c,
            campaign_trigger_message:
              c.campaign_id === campaignId
                ? [...c.campaign_trigger_message, ...messages]
                : c.campaign_trigger_message,
          }))
        },
      )
      .addCase(bulkAddTriggerMessages.rejected, (state, action) => {
        state.loading.add = false
        state.error.add = action.payload as string
      })

    builder
      .addCase(bulkUpdateTriggerMessages.pending, (state) => {
        state.loading.update = true
        state.error.update = null
      })
      .addCase(
        bulkUpdateTriggerMessages.fulfilled,
        (state, action: PayloadAction<ICampaignTriggerMessage[]>) => {
          state.loading.update = false
          const campaignId = action.payload?.[0].campaign_id
          const messageIds = action.payload.map(
            (m) => m.campaign_trigger_message_id,
          )
          state.campaigns = state.campaigns.map((c) => ({
            ...c,
            campaign_trigger_message:
              c.campaign_id === campaignId
                ? [
                    ...c.campaign_trigger_message.filter(
                      (ctm) =>
                        ctm.campaign_trigger_message_id &&
                        !messageIds.includes(ctm.campaign_trigger_message_id),
                    ),
                    ...action.payload,
                  ]
                : c.campaign_trigger_message,
          }))
        },
      )
      .addCase(bulkUpdateTriggerMessages.rejected, (state, action) => {
        state.loading.update = false
        state.error.update = action.payload as string
      })

    builder
      .addCase(deleteCampaign.pending, (state) => {
        state.loading.delete = true
        state.error.delete = null
      })
      .addCase(deleteCampaign.fulfilled, (state, action) => {
        state.loading.delete = false
        state.campaigns = state.campaigns.filter(
          (campaign) => campaign.campaign_id !== action.payload,
        )
      })
      .addCase(deleteCampaign.rejected, (state, action) => {
        state.loading.delete = false
        state.error.delete = action.payload as string
      })

    builder
      .addCase(bulkDeleteCampaignRecipient.pending, (state) => {
        state.loading.delete = true
        state.error.delete = null
      })
      .addCase(bulkDeleteCampaignRecipient.fulfilled, (state, action) => {
        state.loading.delete = false
        state.campaigns = state.campaigns.map((c) => ({
          ...c,
          campaign_recipient: c.campaign_recipient.filter(
            (cr) =>
              cr.campaign_recipient_id &&
              !action.payload.includes(cr.campaign_recipient_id),
          ),
        }))
      })
      .addCase(bulkDeleteCampaignRecipient.rejected, (state, action) => {
        state.loading.delete = false
        state.error.delete = action.payload as string
      })

    builder
      .addCase(optOutCampaignRecipient.pending, (state) => {
        state.loading.delete = true
        state.error.delete = null
      })
      .addCase(optOutCampaignRecipient.fulfilled, (state, action) => {
        state.loading.delete = false
        state.campaigns = state.campaigns.map((c) => ({
          ...c,
          campaign_recipient: c.campaign_recipient.filter(
            (cr) => cr.contact_id !== action.payload,
          ),
        }))
      })
      .addCase(optOutCampaignRecipient.rejected, (state, action) => {
        state.loading.delete = false
        state.error.delete = action.payload as string
      })

    builder
      .addCase(deleteCampaignTemplate.pending, (state) => {
        state.loading.delete = true
        state.error.delete = null
      })
      .addCase(deleteCampaignTemplate.fulfilled, (state, action) => {
        state.loading.delete = false
        state.campaignTemplates = state.campaignTemplates.filter(
          (campaign) => campaign.campaign_template_id !== action.payload,
        )
      })
      .addCase(deleteCampaignTemplate.rejected, (state, action) => {
        state.loading.delete = false
        state.error.delete = action.payload as string
      })

    builder
      .addCase(bulkDeleteKeywords.pending, (state) => {
        state.loading.delete = true
        state.error.delete = null
      })
      .addCase(
        bulkDeleteKeywords.fulfilled,
        (state, action: PayloadAction<string[]>) => {
          state.loading.delete = false
          state.campaigns = state.campaigns.map((c) => ({
            ...c,
            campaign_trigger_keyword: c.campaign_trigger_keyword.filter(
              (k) =>
                k.campaign_trigger_keyword_id &&
                !action.payload.includes(k.campaign_trigger_keyword_id),
            ),
          }))
        },
      )
      .addCase(bulkDeleteKeywords.rejected, (state, action) => {
        state.loading.delete = false
        state.error.delete = action.payload as string
      })

    builder
      .addCase(bulkDeleteTriggerMessages.pending, (state) => {
        state.loading.delete = true
        state.error.delete = null
      })
      .addCase(bulkDeleteTriggerMessages.fulfilled, (state, action) => {
        state.loading.delete = false
        state.campaigns = state.campaigns.map((c) => ({
          ...c,
          campaign_trigger_message: c.campaign_trigger_message.filter(
            (m) =>
              m.campaign_trigger_message_id &&
              !action.payload.includes(m.campaign_trigger_message_id),
          ),
        }))
      })
      .addCase(bulkDeleteTriggerMessages.rejected, (state, action) => {
        state.loading.delete = false
        state.error.delete = action.payload as string
      })

    builder
      .addCase(updateCampaignMessage.pending, (state) => {
        state.loading.update = true
        state.error.update = null
      })
      .addCase(
        updateCampaignMessage.fulfilled,
        (
          state,
          action: PayloadAction<{
            campaign_message_id?: string
            message_sid?: string
            data: Partial<ICampaignMessage>
          }>,
        ) => {
          state.loading.update = false
          const { campaign_message_id, message_sid, data } = action.payload
          state.campaigns = state.campaigns.map((c) => ({
            ...c,
            campaign_message: c.campaign_message.map((m) =>
              m.message_sid === message_sid ||
              m.campaign_message_id === campaign_message_id
                ? { ...m, ...data }
                : m,
            ),
          }))
        },
      )
      .addCase(updateCampaignMessage.rejected, (state, action) => {
        state.loading.update = false
        state.error.update = action.payload as string
      })

    builder
      .addCase(sendCampaignSms.pending, (state) => {
        state.error.add = null
      })
      .addCase(sendCampaignSms.fulfilled, (state, action) => {
        if (action.payload.length > 0) {
          const campaignId = action.payload[0].campaign_id
          state.campaigns = appendMessagesWithContact(
            state.campaigns,
            campaignId,
            action.payload,
          )
        }
      })
      .addCase(sendCampaignSms.rejected, (state, action) => {
        state.error.add = action.payload as string
      })

    builder
      .addCase(scheduleSendSms.pending, (state) => {
        state.error.add = null
      })
      .addCase(scheduleSendSms.fulfilled, (state, action) => {
        if (action.payload.length > 0) {
          const campaignId = action.payload[0].campaign_id
          state.campaigns = appendMessagesWithContact(
            state.campaigns,
            campaignId,
            action.payload,
          )
        }
      })
      .addCase(scheduleSendSms.rejected, (state, action) => {
        state.error.add = action.payload as string
      })

    builder
      .addCase(sendCampaignEmail.pending, (state) => {
        state.error.add = null
      })
      .addCase(sendCampaignEmail.fulfilled, (state, action) => {
        if (action.payload.length > 0) {
          const campaignId = action.payload[0].campaign_id
          state.campaigns = state.campaigns.map((c) => ({
            ...c,
            campaign_message:
              c.campaign_id === campaignId
                ? [...c.campaign_message, ...action.payload]
                : c.campaign_message,
          }))
        }
      })
      .addCase(sendCampaignEmail.rejected, (state, action) => {
        state.error.add = action.payload as string
      })

    builder
      .addCase(scheduleSendEmail.pending, (state) => {
        state.error.add = null
      })
      .addCase(scheduleSendEmail.fulfilled, (state, action) => {
        if (action.payload.length > 0) {
          const campaignId = action.payload[0].campaign_id
          state.campaigns = state.campaigns.map((c) => ({
            ...c,
            campaign_message:
              c.campaign_id === campaignId
                ? [...c.campaign_message, ...action.payload]
                : c.campaign_message,
          }))
        }
      })
      .addCase(scheduleSendEmail.rejected, (state, action) => {
        state.error.add = action.payload as string
      })

    builder
      .addCase(resendBouncedSms.pending, (state, action) => {
        state.loading.update = true
        state.error.update = null
        if (state.campaigns) {
          const params = action.meta.arg
          const index = state.campaigns.findIndex((el) =>
            el.campaign_message.some(
              (m) => m.message_sid === params.message_sid,
            ),
          )

          if (index !== -1) {
            const campaignMessage = state.campaigns[index].campaign_message
            state.campaigns[index].campaign_message = campaignMessage.map(
              (m) =>
                m.message_sid === params.message_sid
                  ? { ...m, status: CampaignMessageStatus.pending }
                  : m,
            )
          }
        }
      })
      .addCase(
        resendBouncedSms.fulfilled,
        (state, action: PayloadAction<Partial<ICampaignMessage>>) => {
          state.loading.update = false
          const messageSID = action.payload.message_sid
          const campaignMessageId = action.payload.campaign_message_id
          if (messageSID || campaignMessageId) {
            state.campaigns = state.campaigns.map((c) => ({
              ...c,
              campaign_message: c.campaign_message.map((m) =>
                m.message_sid === messageSID ||
                m.campaign_message_id === campaignMessageId
                  ? { ...m, ...action.payload }
                  : m,
              ),
            }))
          }
        },
      )
      .addCase(resendBouncedSms.rejected, (state, action) => {
        state.loading.update = false
        state.error.update = action.payload as string
      })

    builder.addCase(setSelectedDate.fulfilled, (state, action) => {
      state.selectedDate = action.payload
    })

    builder.addCase(setShowTemplatePreview.fulfilled, (state, action) => {
      state.showTemplatePreview = action.payload
    })

    builder.addCase(setTemplate.fulfilled, (state, action) => {
      const { template, type } = action.payload
      if (type === 'selected') {
        state.selectedTemplate = template
          ? {
              ...template,
              url:
                template.url && template.url.length > 0
                  ? template.url
                  : undefined,
            }
          : null
        if (template) {
          if (template.type === CampaignTemplateType.saved) {
            state.currentTemplate = {
              ...template,
              url:
                template.url && template.url.length > 0
                  ? template.url
                  : undefined,
            }
          } else {
            state.currentTemplate = {
              channel: template.channel,
              name: '',
              type: CampaignTemplateType.saved,
              text_content: template.text_content,
              html_content: template.html_content,
              created_by: template.created_by,
              date_time_created_at: new Date().toISOString(),
              date_time_updated_at: new Date().toISOString(),
              ...(template.email_editor_canvas
                ? { email_editor_canvas: template.email_editor_canvas }
                : {}),
            }
          }
        } else {
          state.currentTemplate = null
        }
      } else {
        if (state.error.template) state.error.template = null
        state.currentTemplate = template
      }
      if (template === null) {
        state.showTemplatePreview = false
        state.error.template = null
        state.error.hyperlink = null
        state.templateEditorCanvasPreview = false
      }
    })

    builder.addCase(setTemplateError.fulfilled, (state, action) => {
      if (action.payload.type === 'template') {
        state.error.template = action.payload.error
      } else if (action.payload.type === 'hyperlink') {
        state.error.hyperlink = action.payload.error
      }
    })
  },
})

export const {
  setTemplatesListChannel,
  pulseTemplateEditorPreview,
  pulseTemplateEditorResumeEdit,
  setTemplateEditorCanvasPreview,
  setOverviewDateFilter,
} = marketingSlice.actions

export default marketingSlice.reducer
